import { Component, Inject, Injectable, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener, MatTreeModule } from '@angular/material/tree';
import { BehaviorSubject, forkJoin, Subscription } from 'rxjs';
import { AnnualPlanService } from '../../providers/annual-plans.services';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TeamsProvider } from "../../../teams/providers/teams-provider";
import { AthletesProvider } from "../../../athletes/providers/athletes-provider";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { AnnualPlan } from "../../../common/models/annual-plan";
import { Organization } from "../../../common/models/organization";
import { Team } from "../../../common/models/team";
import { Athlete } from "../../../common/models/athlete";
import { User } from "../../../common/models/user";
import { LessThanNumberAnnualPlanValidator } from "../../../common/validators/less-than-number-annual-plan.validator";
import {
  GreaterThanNumberAnnualPlanValidator
} from "../../../common/validators/greater-than-number-annual-plan.validator";
import { MatError, MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule, MatIconButton } from "@angular/material/button";
import { NgForOf, NgIf } from "@angular/common";
import { MatInputModule } from "@angular/material/input";
import { FlexLayoutModule } from "@angular/flex-layout";
import { CenterLoaderComponent } from "../../../common/components/center-loader/center-loader";

/**
 * Node for to-do item
 */
export class TeamAthleteItemNode {
  public children: TeamAthleteItemNode[] = [];
  public item: string = '';
  public object: any;
}

/** Flat to-do item node with expandable and level information */
export class TeamAthleteItemFlatNode {
  public item: string = '';
  public level: number = 0;
  public expandable: boolean = false;
  public object: any;
}

/**
 * Checklist database, it can build a tree structured Json object.
 * Each node in Json object represents a to-do item or a category.
 * If a node is a category, it has children items and new items can be added under the category.
 */
@Injectable({providedIn: 'root'})
export class ChecklistDatabase {
  public dataChange = new BehaviorSubject<TeamAthleteItemNode[]>([]);
  public fullData: TeamAthleteItemNode[] = [];

  get data(): TeamAthleteItemNode[] { return this.dataChange.value; }

  constructor(private teamProvider: TeamsProvider, private athleteProvider: AthletesProvider, private utils: UtilsProvider, private translater: TranslateService, @Inject(MAT_DIALOG_DATA) public dialogData: any) {
    this.initialize();
  }

  public initialize() {
    forkJoin([
      this.teamProvider.getTeams({organizationId: this.dialogData.organization.id}),
      this.athleteProvider.getAthletes(this.dialogData.organization.id)
    ]).subscribe({
      next: ([teams, athleteUsers]) => {
        const treeData: any[] = [];

        // ajoute la liste complète des athlètes (sans team)
        const leafsAthletes: any[] = [];
        let hasChildAthletes = false;
        athleteUsers.map((athleteUser) => {
          leafsAthletes[athleteUser.id!] = {value: athleteUser.firstName + ' ' + athleteUser.lastName, object: athleteUser};
          hasChildAthletes = true;
        });
        if (hasChildAthletes) {
          treeData[this.translater.instant('All_athletes_list')] = leafsAthletes.sort((a, b) => {
            a = this.utils.normalizeString(a.value);
            b = this.utils.normalizeString(b.value);

            return (a > b) ? 1 : -1;
          });
        }

        // ajoute la liste des teams et leurs athlètes
        teams.map((team) => {
          const leafs: any[] = [];
          let hasChild = false;
          team?.athletes?.map((athlete) => {
            leafs[athlete.user!.id!] = {value: athlete.user!.firstName + ' ' + athlete.user!.lastName, object: athlete.user};
            hasChild = true;
            if (hasChild) {
              const index:any = team.name;
              treeData[index] = leafs.sort((a, b) => {
                a = this.utils.normalizeString(a.value);
                b = this.utils.normalizeString(b.value);

                return (a > b) ? 1 : -1;
              });
            }
          });
        });
        // Build the tree nodes from Json object. The result is a list of `TeamAthleteItemNode` with nested
        // file node as children.

        const fileTree = this.buildFileTree(treeData, 0);
        this.fullData = fileTree; // Sauvegarde des données complètes
        // Notify the change.
        this.dataChange.next(fileTree);

      },
    })
  }


  /**
   * Build the file structure tree. The `value` is the Json object, or a sub-tree of a Json object.
   * The return value is the list of `TeamAthleteItemNode`.
   */
  public buildFileTree(obj: {[key: string]: any}, level: number): TeamAthleteItemNode[] {
    return Object.keys(obj).reduce<TeamAthleteItemNode[]>((accumulator, key) => {
      const value = obj[key].value || obj[key];
      const node = new TeamAthleteItemNode();
      node.item = key;
      node.object = obj[key].object;

      if (value != null) {
        if (typeof value === 'object') {
          node.children = this.buildFileTree(value, level + 1);
        } else {
          node.item = value;
        }
      }

      return accumulator.concat(node);
    }, []);
  }
}

@Component({
  selector: 'annual-plan-dialog-attribute',
  templateUrl: './annual-plan-dialog-form-attribute.component.html',
  styleUrls: ['./annual-plan-dialog-form-attribute.scss'],
  standalone: true,
  imports: [
    TranslateModule,
    MatFormFieldModule,
    MatSelectModule,
    MatDatepickerModule,
    MatTreeModule,
    MatCheckboxModule,
    MatDialogModule,
    MatIconModule,
    MatError,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconButton,
    NgIf,
    NgForOf,
    MatInputModule,
    FlexLayoutModule,
    CenterLoaderComponent
  ],
  providers: [ChecklistDatabase]
})
export class AnnualPlanDialogFormAttributeComponent implements OnInit, OnDestroy {

  get f() {
    return this.attributePlanForm.controls;
  }
  public attributePlanForm: FormGroup;
  public isSubmitting = false;
  public annualPlans: AnnualPlan[] = [];
  public annualPlan: AnnualPlan = {} as AnnualPlan;
  public organization: Organization = {} as Organization;
  public startDate: Date = new Date();
  public teamsAthletes: any = [];
  public teams: Team[] = [];
  public athletes: Athlete[] = [];
  public users: User[] = [];
  public rows: any;
  public test: any;

  public minDate: any;

  public weekNumbers: WeekMenu[] = [];

  public treeControl: FlatTreeControl<TeamAthleteItemFlatNode>;

  public dataSource: MatTreeFlatDataSource<TeamAthleteItemNode, TeamAthleteItemFlatNode>;
  public checklistValidated = true;

  /** Map from flat node to nested node. This helps us finding the nested node to be modified */
  private flatNodeMap = new Map<TeamAthleteItemFlatNode, TeamAthleteItemNode>();

  /** Map from nested node to flattened node. This helps us to keep the same object for selection */
  private nestedNodeMap = new Map<TeamAthleteItemNode, TeamAthleteItemFlatNode>();

  /** A selected parent node to be inserted */
  private selectedParent: TeamAthleteItemFlatNode | null = null;

  /** The new item's name */
  private newItemName = '';

  private treeFlattener: MatTreeFlattener<TeamAthleteItemNode, TeamAthleteItemFlatNode>;

  /** The selection for checklist */
  protected checklistSelection = new SelectionModel<TeamAthleteItemFlatNode>(true /* multiple */);
  private subscriptions: Subscription[] = [];

  constructor(
    public dialog: MatDialogRef<AnnualPlanDialogFormAttributeComponent>,
    private teamProvider: TeamsProvider,
    private athleteProvider: AthletesProvider,
    private annualPlanProvider: AnnualPlanService,
    private utils: UtilsProvider,
    private fb: FormBuilder,
    private database: ChecklistDatabase,
    private toaster: MatSnackBar,
    private translator: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: any) {

    this.organization = data.organization;
    this.annualPlan = data.annualPlan;

    // use the FormBuilder to create a form group
    this.attributePlanForm = this.fb.group({
      exportFrom: new FormControl(null, [Validators.required]),
      exportTo: new FormControl(null, [Validators.required]),
      importStartDate: new FormControl(null, [Validators.required]),
      athletes: this.fb.array([], [Validators.required])
    }, {
      validator: [
        LessThanNumberAnnualPlanValidator('exportFrom', 'exportTo', true),
        GreaterThanNumberAnnualPlanValidator('exportTo', 'exportFrom', true),
      ]
    });

    this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel,
      this.isExpandable, this.getChildren);
    this.treeControl = new FlatTreeControl<TeamAthleteItemFlatNode>(this.getLevel, this.isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    this.subscriptions.push(
      database.dataChange.subscribe((databaseData) => {
        this.dataSource.data = databaseData;
        this.checklistSelection.clear();
      })
    );


    this.weekNumbers = data.weeks.map(function(week: any) {
      return {
        number: week.weekNumber,
        realNumber: week.realWeekNumber,
        realWeekYear: week.realWeekYear,
      };
    }.bind(this));
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public filterAthletes(event: any): void {
    const filterValue = event.target.value.toLowerCase();

    // Sauvegarde les identifiants des nœuds actuellement cochés
    const selectedNodeIds = new Set<string>();
    this.checklistSelection.selected.forEach((node) => {
      if (node.object?.id) {
        selectedNodeIds.add(node.object.id.toString());
      }
    });

    // Filtre l'arbre
    const filteredData = this.database.data
      .map((node) => this.filterTree(node, filterValue))
      .filter((node): node is TeamAthleteItemNode => node !== null);

    // Applique le résultat filtré à la source de données
    this.dataSource.data = filteredData;

    // Restaure les états des cases cochées
    this.treeControl.dataNodes.forEach((node) => {
      if (node.object?.id && selectedNodeIds.has(node.object.id.toString())) {
        this.checklistSelection.select(node);
      }
    });
  }


  private filterTree(node: TeamAthleteItemNode, filterValue: string): TeamAthleteItemNode | null {
    // Vérifie si le nœud correspond au filtre
    const matches = node.item.toLowerCase().includes(filterValue);

    // Filtre récursivement les enfants
    const filteredChildren = node.children
      .map((child) => this.filterTree(child, filterValue))
      .filter((child): child is TeamAthleteItemNode => child !== null);

    // Conserve le nœud si lui ou ses enfants correspondent au filtre
    if (matches || filteredChildren.length > 0) {
      return { ...node, children: filteredChildren };
    }

    // Exclut le nœud s'il ne correspond pas
    return null;
  }

  public buildFileTree(obj: { [key: string]: any }, level: number): TeamAthleteItemNode[] {
    return Object.keys(obj).reduce<TeamAthleteItemNode[]>((accumulator, key) => {
      const value = obj[key].value || obj[key];
      const node = new TeamAthleteItemNode();
      node.item = key;
      node.object = obj[key].object || {};
      console.log(node);

      if (value != null && typeof value === 'object') {
        node.children = this.buildFileTree(value, level + 1);
      }

      return accumulator.concat(node);
    }, []);
  }


  public compareWeekMenus(o1: WeekMenu, o2: WeekMenu): boolean {
    if (null === o1 || null === o2) {
      return false;
    }

    return o1.number === o2.number;
  }

  /** Whether all the descendants of the node are selected. */
  public descendantsAllSelected(node: TeamAthleteItemFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    return descendants.every((child) =>
      this.checklistSelection.isSelected(child)
    );
  }

  /** Whether part of the descendants are selected */
  public descendantsPartiallySelected(node: TeamAthleteItemFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const result = descendants.some((child) => this.checklistSelection.isSelected(child));
    return result && !this.descendantsAllSelected(node);
  }

  /** Toggle the to-do item selection. Select/deselect all the descendants node */
  public todoItemSelectionToggle(node: TeamAthleteItemFlatNode): void {
    this.checklistSelection.toggle(node);
    const descendants = this.treeControl.getDescendants(node);
    this.checklistSelection.isSelected(node)
      ? this.checklistSelection.select(...descendants)
      : this.checklistSelection.deselect(...descendants)
    ;

    for (const child of descendants) {
      this.checklistSelection.isSelected(child);
    }

    this.checkAllParentsSelection(node);
  }

  /** Toggle a leaf to-do item selection. Check all the parents to see if they changed */
  public todoLeafItemSelectionToggle(node: TeamAthleteItemFlatNode, event: any): void {
    // add users to form values
    // this.addAthleteToForm(node.object, event.checked);

    this.checklistSelection.toggle(node);
    this.checkAllParentsSelection(node);
  }

  /* Checks all the parents when a leaf node is selected/unselected */
  public checkAllParentsSelection(node: TeamAthleteItemFlatNode): void {
    let parent: TeamAthleteItemFlatNode | null = this.getParentNode(node);
    while (parent) {
      this.checkRootNodeSelection(parent);
      parent = this.getParentNode(parent);
    }
    this.validCheckListSelection();
  }

  /** Check root node checked state and change it accordingly */
  public checkRootNodeSelection(node: TeamAthleteItemFlatNode): void {
    const nodeSelected = this.checklistSelection.isSelected(node);
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected = descendants.every((child) =>
      this.checklistSelection.isSelected(child)
    );
    if (nodeSelected && !descAllSelected) {
      this.checklistSelection.deselect(node);
    } else if (!nodeSelected && descAllSelected) {
      this.checklistSelection.select(node);
    }
    this.validCheckListSelection();
  }

  /* Get the parent node of a node */
  public getParentNode(node: TeamAthleteItemFlatNode): TeamAthleteItemFlatNode | null {
    const currentLevel = this.getLevel(node);

    if (currentLevel < 1) {
      return null;
    }

    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];

      if (this.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }

    return null;
  }

  public ngOnInit(): void {

  }

  public validCheckListSelection() {
    this.checklistValidated = !(this.checklistSelection.selected.length === 0);
  }
  public submitForm() {
    this.validCheckListSelection();

    if (this.checklistValidated) {
      const athletes = this.attributePlanForm.get('athletes') as FormArray;

      // remove all selected athletes
      while (athletes.length) {
        athletes.removeAt(athletes.length - 1);
      }

      // add selected athletes to form, avoiding duplicates
      this.checklistSelection.selected.forEach((selected) => {
        if (selected.object) {
          const alreadyExists = athletes.controls.some(
            (control) => control.value.id === selected.object.id
          );

          if (!alreadyExists) {
            athletes.push(this.fb.control(selected.object, [Validators.required]));
          }
        }
      });

      this.isSubmitting = true;

      if (this.attributePlanForm.valid) {
        this.isSubmitting = false;

        this.subscriptions.push(
          this.annualPlanProvider.export(
              this.organization,
              this.annualPlan,
              this.attributePlanForm.controls['athletes'].value,
              this.attributePlanForm.controls['exportFrom'].value,
              this.attributePlanForm.controls['exportTo'].value,
              this.utils.DateToYmdHis(new Date(this.attributePlanForm.controls['importStartDate'].value), false),
            ).subscribe(
              () => {
                this.dialog.close(true);
              },
              (errorResp) => {
                let message = (errorResp.error.errors || [])
                  .map((error: string) => this.translator.instant(error))
                  .join('\n');

                this.toaster.open(message, '', { duration: 5000 });
              }
            )
        );
      } else {
        this.utils.validateAllFormFields(this.attributePlanForm);
        this.isSubmitting = false;
        this.utils.scrollToFormInputError();
      }
    } else {
      this.utils.scrollToFormInputError();
    }
  }

  public hasChild = (_: number, _nodeData: TeamAthleteItemFlatNode) => _nodeData.expandable;

  public hasNoContent = (_: number, _nodeData: TeamAthleteItemFlatNode) => _nodeData.item === '';

  /**
   * Transformer to convert nested node to flat node. Record the nodes in maps for later use.
   */
  private transformer = (node: TeamAthleteItemNode, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode = existingNode && existingNode.item === node.item
      ? existingNode
      : new TeamAthleteItemFlatNode();
    flatNode.item = node.item;
    flatNode.level = level;
    flatNode.expandable = node.children.length > 0;
    flatNode.object = node.object;
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  };

  private getLevel = (node: TeamAthleteItemFlatNode) => node.level;

  private isExpandable = (node: TeamAthleteItemFlatNode) => node.expandable;

  private getChildren = (node: TeamAthleteItemNode): TeamAthleteItemNode[] => node.children;

  private getObject = (node: TeamAthleteItemNode): TeamAthleteItemNode[] => node.object;

  /**
   * Set endDate date picker min and max available dates
   */
  private updateMinMaxPickerDate() {
    // annual plan must be at least 4 months length
    this.minDate = this.attributePlanForm.get('exportFrom')?.value;
  }
}

export interface WeekMenu {
  number: number;
  realNumber: number;
  realWeekYear: number;
  isDisabled: boolean;
}
